/**
 * Keep React alive when another script has moved the nodes it owns.
 *
 * Chrome's page translation (and some extensions) replace text nodes under React
 * with <font> wrappers. React still holds the original nodes, so the next time it
 * removes one it calls `parent.removeChild(node)` on a parent that no longer
 * contains it, the browser throws NotFoundError, and the page's error boundary
 * swaps the whole page for its fallback. 72 rows over 27 days to 2026-09-23
 * (af4945df3284063f), 22 of 24 on Android Chrome product pages, several from
 * Indian publisher partners -- and 2 of 25 of those people kept browsing
 * afterwards against a 16% baseline.
 *
 * The guard is the long-standing fix for facebook/react#11538: a removal or an
 * insert against a node that is not actually a child is skipped instead of thrown.
 * Every skip is reported once per page load, with whether the page was
 * translated, so a real React bug of the same shape stays visible. It never logs
 * through console.error, which the error interceptor would file as a fresh error.
 */

export const DOM_SKIP_MESSAGE = 'DOM mutation skipped: node was moved by another script';

export interface DomSkipReport {
  op: 'removeChild' | 'insertBefore';
  parentTag: string;
  /** Where the node actually is now -- FONT for a translated page. */
  nodeParentTag: string;
  /** Chrome marks a translated page with translated-ltr / translated-rtl on <html>. */
  translated: boolean;
  htmlLang: string;
  navigatorLanguage: string;
  /**
   * The node React tried to move, as `TAG#id.class`. An untranslated skip with an
   * empty nodeParentTag means something detached the node first; without these
   * the row could not say whose node it was (d7afbab106c9e3c9).
   */
  node: string;
  parent: string;
  /** The parent's nearest ancestors, innermost first, joined with " < ". */
  parentPath: string;
}

const DESCRIBE_LIMIT = 120;
const PATH_DEPTH = 4;

/** `TAG#id.class.class` for an element, `#text` and friends for anything else. */
export function describeNode(node: Node | null | undefined): string {
  if (!node) return '';
  if (node.nodeType !== 1) return node.nodeName;
  const el = node as Element;
  const id = el.id ? `#${el.id}` : '';
  const classes = typeof el.className === 'string' ? el.className.trim().split(/\s+/).filter(Boolean) : [];
  const cls = classes.map((c) => `.${c}`).join('');
  return `${el.nodeName}${id}${cls}`.slice(0, DESCRIBE_LIMIT);
}

function ancestorPath(node: Node): string {
  const parts: string[] = [];
  for (let cur = node.parentNode; cur && cur.nodeType === 1 && parts.length < PATH_DEPTH; cur = cur.parentNode) {
    parts.push(describeNode(cur));
  }
  return parts.join(' < ');
}

type GuardedWindow = Pick<Window, 'document' | 'navigator'> & { Node: typeof Node };

const installed = new WeakSet<object>();

/**
 * Patch `win.Node.prototype` once. Returns a function that restores it.
 *
 * @param report called at most once per operation per page load
 */
export function installDomMutationGuard(win: GuardedWindow, report: (detail: DomSkipReport) => void): () => void {
  const proto = win.Node.prototype;
  if (installed.has(proto)) return () => {};

  const originalRemoveChild = proto.removeChild;
  const originalInsertBefore = proto.insertBefore;
  const reported = new Set<DomSkipReport['op']>();

  const skip = (op: DomSkipReport['op'], parent: Node, node: Node) => {
    if (reported.has(op)) return;
    reported.add(op);
    const html = win.document.documentElement;
    try {
      report({
        op,
        parentTag: parent.nodeName,
        nodeParentTag: node.parentNode?.nodeName ?? '',
        translated: html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl'),
        htmlLang: html.lang || '',
        navigatorLanguage: win.navigator.language, // every browser window has a navigator
        node: describeNode(node),
        parent: describeNode(parent),
        parentPath: ancestorPath(parent),
      });
    } catch {
      // Reporting must never be what breaks the page this is keeping alive.
    }
  };

  proto.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      skip('removeChild', this, child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  proto.insertBefore = function insertBefore<T extends Node>(this: Node, node: T, ref: Node | null): T {
    if (ref && ref.parentNode !== this) {
      skip('insertBefore', this, ref);
      return node;
    }
    return originalInsertBefore.call(this, node, ref) as T;
  };

  installed.add(proto);
  return () => {
    proto.removeChild = originalRemoveChild;
    proto.insertBefore = originalInsertBefore;
    installed.delete(proto);
  };
}
